/**
 * Iframe JWT Token Communication Utilities
 * Functions to send JWT tokens from react-js parent window to lovable-aligned iframe
 */

import { isIframeOriginAllowed } from '../security/config';

/**
 * Send JWT token to iframe
 * @param iframeRef - Reference to the iframe element
 * @param jwtToken - The JWT token to send
 * @param targetOrigin - The origin of the iframe (use '*' for any origin, or specific origin for security)
 */
export const sendJwtTokenToIframe = (
  iframeRef,
  jwtToken,
  targetOrigin = '*',
) => {
  if (!iframeRef?.contentWindow || !jwtToken) return;

  try {
    iframeRef.contentWindow.postMessage(
      { type: 'SET_JWT_TOKEN', payload: { jwtToken } },
      targetOrigin,
    );
  } catch (error) {
    // Silently fail - iframe may not be ready
  }
};

/**
 * Clear JWT token in iframe
 * @param iframeRef - Reference to the iframe element
 * @param targetOrigin - The origin of the iframe
 */
export const clearJwtTokenInIframe = (iframeRef, targetOrigin = '*') => {
  if (!iframeRef?.contentWindow) return;

  try {
    iframeRef.contentWindow.postMessage(
      { type: 'CLEAR_JWT_TOKEN' },
      targetOrigin,
    );
  } catch (error) {
    // Silently fail - iframe may not be ready
  }
};

/**
 * Send SSO token to iframe with retry logic
 * @param iframeRef - Reference to the iframe element
 * @param ssoToken - The SSO token to send
 * @param targetOrigin - The origin of the iframe
 * @param retries - Number of retry attempts (default 3)
 * @param delay - Delay between retries in ms (default 1000)
 */
export const sendSsoTokenToIframe = (
  iframeRef,
  ssoToken,
  targetOrigin = '*',
  retries = 3,
  delay = 1000,
) => {
  if (!iframeRef?.contentWindow || !ssoToken) return;

  const attempt = (remaining) => {
    try {
      iframeRef.contentWindow.postMessage(
        { type: 'SET_SSO_TOKEN', payload: { ssoToken } },
        targetOrigin,
      );
    } catch (error) {
      if (remaining > 0) {
        setTimeout(() => attempt(remaining - 1), delay);
      }
    }
  };

  attempt(retries);
};

/**
 * Setup message listener to respond to token requests from iframe.
 * Stores pending requests so tokens can be sent once available.
 * @param jwtTokenGetter - Function that returns the current JWT token
 * @param ssoTokenGetter - Function that returns the current SSO token
 * @returns Cleanup function to remove the event listener
 */
export const setupIframeTokenListener = (
  jwtTokenGetter,
  ssoTokenGetter = null,
) => {
  // Track pending requests from iframe so we can fulfill them when tokens arrive
  const pendingRequests = { jwt: null, sso: null };

  const trySendPending = () => {
    if (pendingRequests.sso) {
      const ssoToken = ssoTokenGetter?.();
      if (ssoToken) {
        const { source, origin } = pendingRequests.sso;
        if (source) {
          try {
            source.postMessage(
              { type: 'SET_SSO_TOKEN', payload: { ssoToken } },
              origin,
            );
            pendingRequests.sso = null;
          } catch (e) {
            // source window may have been closed/navigated
          }
        }
      }
    }
    if (pendingRequests.jwt) {
      const jwtToken = jwtTokenGetter();
      if (jwtToken) {
        const { source, origin } = pendingRequests.jwt;
        if (source) {
          try {
            source.postMessage(
              { type: 'SET_JWT_TOKEN', payload: { jwtToken } },
              origin,
            );
            pendingRequests.jwt = null;
          } catch (e) {
            // source window may have been closed/navigated
          }
        }
      }
    }
  };

  const handleMessage = (event) => {
    // Security: Validate origin FIRST
    if (!isIframeOriginAllowed(event.origin)) {
      return;
    }

    if (event.data?.type === 'REQUEST_JWT_TOKEN') {
      const jwtToken = jwtTokenGetter();
      if (jwtToken && event.source) {
        event.source.postMessage(
          { type: 'SET_JWT_TOKEN', payload: { jwtToken } },
          event.origin,
        );
      } else {
        // Token not ready yet - store the request to fulfill later
        pendingRequests.jwt = { source: event.source, origin: event.origin };
      }
    }

    if (event.data?.type === 'REQUEST_SSO_TOKEN') {
      const ssoToken = ssoTokenGetter?.();
      if (ssoToken && event.source) {
        event.source.postMessage(
          { type: 'SET_SSO_TOKEN', payload: { ssoToken } },
          event.origin,
        );
      } else {
        // Token not ready yet - store the request to fulfill later
        pendingRequests.sso = { source: event.source, origin: event.origin };
      }
    }
  };

  window.addEventListener('message', handleMessage);

  // Periodically check if pending requests can be fulfilled
  const intervalId = setInterval(trySendPending, 500);

  // Return cleanup function
  return () => {
    window.removeEventListener('message', handleMessage);
    clearInterval(intervalId);
  };
};
